import numbro from 'numbro';
import React, { useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { transactionActions } from '../../actions';
import LoadingImage from '../../components/general/LoadingImage';
import PlanCardDiscountSwitch from '../../components/PlanCardDiscountSwitch';
import {
  isMonthRestricted,
  priceReady,
  totalAmountRaw,
  usePrices,
  usePromo,
} from '../../helpers/price.helper';
import useDispatch from '../../hooks/useDispatch';
import { useSandboxPayment } from '../../hooks/useSandboxPayment';
import {
  StripeCoupon,
  V1Coupon,
} from '../../py-generated/datalouder/checkout/types';

const PromoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 1rem 0;

  .form-label {
    font-size: 0.9rem;
    white-space: nowrap;
    margin: 0;
    margin-right: 0.5rem;
  }

  button {
    white-space: nowrap;
    margin-left: 0.5rem;
  }
`;

export type MonthOptions = 1 | 6 | 12;

interface Props {
  freeTrialAvailable: boolean;
  month: MonthOptions;
  setMonth: (m: MonthOptions) => void;
  disablePromo?: boolean;
}

const PaymentSelection: React.FC<Props> = ({
  freeTrialAvailable,
  month,
  setMonth,
  disablePromo,
}) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const {
    PRICE_PER_MONTH,
    DISCOUNT_FOR_12_MONTH,
    loading: loadingPrices,
  } = usePrices();

  const { promo, stripeCoupon, requestedPromo, loadingPromo, clearPromo } =
    usePromo();

  const [inputPromo, setInputPromo] = useState('');

  const onSwitchMonth = (_month: MonthOptions) => {
    if (isMonthRestricted(_month, stripeCoupon)) {
      clearPromo();
    } else {
      setMonth(_month);
    }
  };

  const usingSandboxPayment = useSandboxPayment();

  return (
    <>
      <PlanCardDiscountSwitch
        selectedMonth={month}
        handleSwitch={onSwitchMonth}
        isMonthRestricted={m => isMonthRestricted(m, stripeCoupon)}
      />
      <Table size="sm" style={{ fontSize: '0.9rem', marginTop: 30 }}>
        <thead>
          <tr>
            <th>{t('Item')}</th>
            <th>{t('Number')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ fontSize: '0.8rem' }}>
              {t('Membership')} (HKD{' '}
              {monthlyAmountLabel(
                PRICE_PER_MONTH,
                DISCOUNT_FOR_12_MONTH,
                loadingPrices,
                month
              )}{' '}
              {t('/ Month')})
            </td>
            <td>{month}</td>
          </tr>
          <tr>
            <th colSpan={2} style={{ textAlign: 'center' }}>
              <i>
                {t('avg-day-payment')}{' '}
                {dailyAmountLabel(
                  PRICE_PER_MONTH,
                  DISCOUNT_FOR_12_MONTH,
                  loadingPrices,
                  month
                )}
                .
              </i>
            </th>
          </tr>
          {freeTrialAvailable && (
            <tr>
              <td colSpan={2} style={{ textAlign: 'center' }}>
                <small>{t('charge-time-for-free-trial')}</small>
              </td>
            </tr>
          )}
          {usingPromoCode(promo, stripeCoupon) && (
            <tr>
              <th>
                <s>{t('Total')}</s>
              </th>
              <th>
                <s>
                  {totalAmountWithoutPromoLabel(
                    PRICE_PER_MONTH,
                    DISCOUNT_FOR_12_MONTH,
                    loadingPrices,
                    month
                  )}
                </s>
              </th>
            </tr>
          )}
          <tr>
            <th>
              {usingPromoCode(promo, stripeCoupon) ? t('Promo') : t('Total')}
            </th>
            <th>
              {totalAmountLabel(
                PRICE_PER_MONTH,
                DISCOUNT_FOR_12_MONTH,
                loadingPrices,
                month,
                promo,
                stripeCoupon
              )}
            </th>
          </tr>
        </tbody>
      </Table>

      {!disablePromo && (
        <Form
          onSubmit={e => {
            e.preventDefault();
            dispatch(
              transactionActions.getPromo(inputPromo, usingSandboxPayment)
            );
          }}
        >
          <PromoInfo
            loadingPromo={loadingPromo}
            promo={promo}
            stripeCoupon={stripeCoupon}
            requestedPromo={requestedPromo}
          />
          <PromoRow>
            <Form.Label>{t('promo-code')}</Form.Label>
            <Form.Control
              size="sm"
              placeholder="XXXXXX"
              value={inputPromo}
              onChange={({ target: { value } }) => setInputPromo(value)}
            />
            <Button size="sm" variant="outline-primary" type="submit">
              {t('apply')}
            </Button>
          </PromoRow>
        </Form>
      )}
    </>
  );
};

interface PromoInfoProps {
  loadingPromo: boolean;
  promo: V1Coupon | undefined;
  stripeCoupon: StripeCoupon | undefined;
  requestedPromo: string;
}

const PromoInfo: React.FC<PromoInfoProps> = ({
  loadingPromo,
  promo,
  stripeCoupon,
  requestedPromo,
}) => {
  const { i18n } = useTranslation();
  if (loadingPromo) {
    return <LoadingImage displayAsBlock small loading />;
  }
  let promoName = null;
  if (stripeCoupon) {
    promoName = stripeCoupon.name;
  } else if (promo) {
    promoName = promo.title[i18n.language];
  }
  if (!promoName) return null;
  return (
    <Alert
      style={{ fontSize: '0.8rem' }}
      variant={promoName ? 'success' : 'danger'}
    >
      {promoName ? (
        <Trans i18nKey="have-applied">{{ title: promoName }}</Trans>
      ) : (
        <Trans i18nKey="invalid-promo">{{ code: requestedPromo }}</Trans>
      )}
    </Alert>
  );
};

const totalAmountWithoutPromoRaw = (
  PRICE_PER_MONTH: number,
  DISCOUNT_FOR_12_MONTH: number,
  loadingPrices: boolean,
  month: MonthOptions
) => {
  if (!priceReady(PRICE_PER_MONTH, DISCOUNT_FOR_12_MONTH, loadingPrices))
    return -1;
  // no promo
  if (month === 1) {
    return 799.99;
  }
  return month <= 10
    ? month * PRICE_PER_MONTH
    : month * PRICE_PER_MONTH * (1 - DISCOUNT_FOR_12_MONTH);
};

const totalAmountLabel = (
  PRICE_PER_MONTH: number,
  DISCOUNT_FOR_12_MONTH: number,
  loadingPrices: boolean,
  month: MonthOptions,
  promo: V1Coupon | undefined,
  stripeCoupon: StripeCoupon | undefined
) => {
  if (loadingPrices) return <LoadingImage loading />;
  return numbro(
    totalAmountRaw(
      PRICE_PER_MONTH,
      DISCOUNT_FOR_12_MONTH,
      loadingPrices,
      month,
      promo,
      stripeCoupon
    )
  ).format('$0,0.00');
};

const totalAmountWithoutPromoLabel = (
  PRICE_PER_MONTH: number,
  DISCOUNT_FOR_12_MONTH: number,
  loadingPrices: boolean,
  month: MonthOptions
) => {
  if (loadingPrices) return <LoadingImage loading />;
  return numbro(
    totalAmountWithoutPromoRaw(
      PRICE_PER_MONTH,
      DISCOUNT_FOR_12_MONTH,
      loadingPrices,
      month
    )
  ).format('$0,0.00');
};

const monthlyAmountLabel = (
  PRICE_PER_MONTH: number,
  DISCOUNT_FOR_12_MONTH: number,
  loadingPrices: boolean,
  month: MonthOptions
) => {
  if (loadingPrices) return <LoadingImage loading />;
  return numbro(
    totalAmountWithoutPromoRaw(
      PRICE_PER_MONTH,
      DISCOUNT_FOR_12_MONTH,
      loadingPrices,
      month
    ) / month
  ).format('$0,0.00');
};

const dailyAmountLabel = (
  PRICE_PER_MONTH: number,
  DISCOUNT_FOR_12_MONTH: number,
  loadingPrices: boolean,
  month: MonthOptions
) => {
  if (loadingPrices) return <LoadingImage loading />;

  return numbro(
    totalAmountWithoutPromoRaw(
      PRICE_PER_MONTH,
      DISCOUNT_FOR_12_MONTH,
      loadingPrices,
      month
    ) /
      month /
      30
  ).format('$0,0.00');
};

const usingPromoCode = (
  promo: V1Coupon | undefined,
  stripeCoupon: StripeCoupon | undefined
) => {
  if (stripeCoupon) {
    return stripeCoupon.promo_code;
  }
  if (promo) {
    return promo.code;
  }
  return null;
};

export default PaymentSelection;
